import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearAuthSession,
  getAuthSession,
  setAuthSession,
} from "../renderer/platform/authStorage";
import apiCall from "../services/apiService";

const AuthContext = createContext(null);

const isAuthenticationFailure = (error) =>
  error?.status === 401 || error?.status === 403;

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(() => getAuthSession());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(getAuthSession()?.token));

  useEffect(() => {
    const token = session?.token;
    if (!token) {
      setUser(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);

    const loadUser = async () => {
      try {
        const response = await apiCall("/auth/me", { signal: controller.signal });
        if (cancelled) return;

        const profile = response?.data ?? null;
        let nextSession = session;
        if (
          profile &&
          typeof profile.role === "string" &&
          profile.role.length > 0 &&
          profile.role !== session.role
        ) {
          nextSession = await setAuthSession(
            token,
            profile.role,
            session.persistent
          );
          if (cancelled) return;
          setSession(nextSession);
        }

        setUser(profile);
      } catch (error) {
        if (cancelled || error?.name === "AbortError") return;

        console.error("Failed to load user:", error);
        if (isAuthenticationFailure(error)) {
          await clearAuthSession();
          if (cancelled) return;
          setSession(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadUser();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [session]);

  const login = useCallback(async (token, userData, persistent = true) => {
    if (!userData || typeof userData.role !== "string") {
      throw new TypeError("Authenticated user data must include a role");
    }

    setLoading(true);
    try {
      const nextSession = await setAuthSession(
        token,
        userData.role,
        persistent
      );
      setSession(nextSession);
      setUser(userData);
      return nextSession;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await clearAuthSession();
    localStorage.removeItem("google_consent_given");
    setSession(null);
    setUser(null);
    setLoading(false);
  }, []);

  const value = useMemo(
    () => ({ session, user, loading, login, logout }),
    [session, user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
};
